import { useEffect, useRef, useState } from 'react'
import { AlbumFile } from '@/models/albumFile.ts'
import { useTrackStat } from '@/contexts/statsScopeContext.tsx'

export enum SelectionMode {
  DEFAULT = 'default',
  DOWNLOAD = 'download',
  SHARE = 'share',
  IMAGE_SELECTION = 'image_selection',
}

/** Result of validating a persisted, selected file during pruning. */
export type FileValidity = 'valid' | 'invalid' | 'unknown'

export interface PruneSelectionOptions {
  /** Whether an album is still present on the drive. */
  isAlbumValid: (albumId: string) => boolean
  /**
   * Whether a selected file still exists. `albumId` is the album the file was
   * selected in (if known). Return 'unknown' when it cannot be verified yet
   * (e.g. the file's album has not finished loading) so it is kept.
   */
  isFileValid: (fileId: string, albumId: string | undefined) => FileValidity
}

export interface FileSelectionType {
  toggleFileSelection: (file: AlbumFile) => void;
  isSelecting: boolean;
  isAllSelected: (albumId: string) => boolean;
  setIsSelecting: (selected: boolean) => void;
  setIsAllSelected: (albumId: string, value: boolean) => void
  getSelectedAlbumFileIds: () => Array<string>
  getSelectedAlbumIds: () => Array<string>
  isFileSelected: (file: AlbumFile, albumId: string | undefined) => boolean
  maxFiles?: number
  setMaxFiles: (maxFiles?: number) => void
  selectionMode: SelectionMode
  setSelectionMode: (mode: SelectionMode) => void
  /** Whether anything (files or whole albums) is currently selected. */
  hasSelection: boolean
  /** Clears the whole selection and leaves selection mode. */
  clearSelection: () => void
  /**
   * Toggles a single file in the selection and makes sure selection mode is
   * active, without resetting the rest of the selection. Used by the per-file
   * heart / double-click. Records the file's album so a persisted selection can
   * later be pruned.
   */
  toggleFileInSelection: (file: AlbumFile, albumId?: string) => void
  /** The album a selected file was picked in (if recorded). */
  getSelectedFileAlbumId: (fileId: string) => string | undefined
  /** Removes selected albums/files that no longer exist. */
  pruneSelection: (options: PruneSelectionOptions) => void
  /**
   * Leaves selection mode because the user cancelled — as opposed to
   * `setIsSelecting(false)`, which is also how a finished selection or a
   * restart from the gallery nav clears things. Only this route is reported
   * (as discarded when something was selected, aborted otherwise).
   */
  abortSelection: () => void
}

const STORAGE_PREFIX = 'heyalbum:selection:'

interface PersistedSelection {
  selectedFiles: Record<string, boolean>
  selectedFileAlbums: Record<string, string>
  allSelectedAlbums: Record<string, boolean>
  isSelecting: boolean
}

function loadPersisted(persistKey?: string): PersistedSelection | null {
  if (!persistKey) {
    return null
  }
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + persistKey)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    return {
      selectedFiles: parsed.selectedFiles ?? {},
      selectedFileAlbums: parsed.selectedFileAlbums ?? {},
      allSelectedAlbums: parsed.allSelectedAlbums ?? {},
      isSelecting: !!parsed.isSelecting,
    }
  } catch {
    return null
  }
}

export default function useFileSelection({ initialIsSelecting, persistKey, trackStats }: {
  initialIsSelecting?: boolean,
  persistKey?: string,
  /**
   * Report the selection events (started / file added / … ). Opt-in, because
   * this hook also drives selections that are not "the customer picks images
   * for the photographer" — the shop's product image picker, for one.
   */
  trackStats?: boolean,
} = {}): FileSelectionType {
  // Read persisted state once on mount (lazy initializer), shared by the states below.
  const [persisted] = useState<PersistedSelection | null>(() => persistKey ? loadPersisted(persistKey) : null)

  const [isSelecting, _setIsSelecting] = useState(
    persisted ? persisted.isSelecting : (initialIsSelecting !== undefined ? initialIsSelecting : false)
  )
  const [allSelectedAlbums, setAllSelectedAlbums] = useState<Record<string, boolean>>(persisted?.allSelectedAlbums ?? {})
  const [selectedFiles, setSelectedFiles] = useState<Record<string, boolean>>(persisted?.selectedFiles ?? {})
  const [selectedFileAlbums, setSelectedFileAlbums] = useState<Record<string, string>>(persisted?.selectedFileAlbums ?? {})
  const [maxFiles, setMaxFiles] = useState<number | undefined>(undefined)
  const [selectionMode, _setSelectionMode] = useState<SelectionMode>(SelectionMode.DEFAULT)
  // Reports nothing outside a stats scope either (portal, B2C drives).
  const trackEvent = useTrackStat()
  const track: typeof trackEvent = trackStats ? trackEvent : () => {}

  // Mirrors `isSelecting` for the start/stop edge detection below. The state
  // variable is captured per render, and the gallery nav restarts a selection
  // via `setIsSelecting(false)` + `setTimeout(…, 0)` — that callback still sees
  // the old `true` and would swallow the `selection_started` of the new run.
  const isSelectingRef = useRef(isSelecting)
  isSelectingRef.current = isSelecting

  // Persist selection to localStorage (prof drives pass a persistKey).
  useEffect(() => {
    if (!persistKey) {
      return
    }
    try {
      const payload: PersistedSelection = { selectedFiles, selectedFileAlbums, allSelectedAlbums, isSelecting }
      localStorage.setItem(STORAGE_PREFIX + persistKey, JSON.stringify(payload))
    } catch {
      // ignore quota / serialization errors
    }
  }, [persistKey, selectedFiles, selectedFileAlbums, allSelectedAlbums, isSelecting])

  function setSelectionMode(mode: SelectionMode) {
    _setSelectionMode(mode)
  }

  /** Enters selection mode, reporting the start only on the false → true edge. */
  function beginSelecting() {
    if (!isSelectingRef.current) {
      track('selection_started')
    }
    isSelectingRef.current = true
    _setIsSelecting(true)
  }

  function setIsSelecting(selected: boolean) {
    // Both entering and leaving selection mode clear the selection. A persisted
    // selection therefore only survives a page reload (where nobody calls this),
    // not an explicit cancel.
    setAllSelectedAlbums({})
    setSelectedFiles({})
    setSelectedFileAlbums({})
    if (!selected) {
      _setSelectionMode(SelectionMode.DEFAULT)
      isSelectingRef.current = false
      _setIsSelecting(false)
      return
    }

    beginSelecting()
  }

  function clearSelection() {
    setIsSelecting(false)
  }

  function abortSelection() {
    // Discarded vs. merely left: only the first one loses something.
    track(hasSelection ? 'selection_deleted' : 'selection_aborted')
    setIsSelecting(false)
  }

  function isAllSelected(albumId: string): boolean {
    return isSelecting && !!allSelectedAlbums[albumId]
  }

  function setIsAllSelected(albumId: string, value: boolean) {
    setAllSelectedAlbums({
      ...allSelectedAlbums,
      [albumId]: value,
    })
    if (value) {
      if (!isSelecting) {
        beginSelecting()
      }
    } else if (allSelectedAlbums[albumId]) {
      // No `files_count`: the gallery's Album has no file count (it only comes
      // with the portal's prof project payload), and the field is optional.
      track('selection_album_removed', { album: albumId })
    }
  }

  function getSelectedAlbumIds(): Array<string> {
    return Object.keys(allSelectedAlbums).filter(albumId => allSelectedAlbums[albumId])
  }

  function isFileSelected(file: AlbumFile, albumId?: string | undefined): boolean {
    return isSelecting && ((albumId && isAllSelected(albumId)) || !!selectedFiles[file.id])
  }

  function toggleFileSelection(file: AlbumFile) {
    const isSelected = isFileSelected(file)
    if (maxFiles !== undefined && !isSelected && getSelectedAlbumFileIds().length + 1 > maxFiles) {
      return
    }
    setSelectedFiles({
      ...selectedFiles,
      [file.id]: !isSelected,
    })
    track(isSelected ? 'selection_file_removed' : 'selection_file_added', { file: file.id })
  }

  function toggleFileInSelection(file: AlbumFile, albumId?: string) {
    const currentlySelected = !!selectedFiles[file.id]
    if (maxFiles !== undefined && !currentlySelected && getSelectedAlbumFileIds().length + 1 > maxFiles) {
      return
    }

    const nextFiles = { ...selectedFiles, [file.id]: !currentlySelected }
    setSelectedFiles(nextFiles)
    track(currentlySelected ? 'selection_file_removed' : 'selection_file_added', { album: albumId, file: file.id })

    if (albumId) {
      setSelectedFileAlbums(prev => {
        const next = { ...prev }
        if (currentlySelected) {
          delete next[file.id]
        } else {
          next[file.id] = albumId
        }
        return next
      })
    }

    const nextFileCount = Object.values(nextFiles).filter(Boolean).length
    const albumCount = getSelectedAlbumIds().length
    // Selecting something enters selection mode. Deselecting the last item does
    // NOT leave it: the bar stays visible in its empty ("nothing selected")
    // state and the user cancels explicitly. Cancelling then shows no warning
    // because hasSelection is false.
    if ((nextFileCount > 0 || albumCount > 0) && !isSelecting) {
      beginSelecting()
    }
  }

  function getSelectedFileAlbumId(fileId: string): string | undefined {
    return selectedFileAlbums[fileId]
  }

  function pruneSelection({ isAlbumValid, isFileValid }: PruneSelectionOptions) {
    // Prune whole-album selections whose album no longer exists.
    let albumsChanged = false
    const nextAlbums: Record<string, boolean> = {}
    for (const [albumId, value] of Object.entries(allSelectedAlbums)) {
      if (!value) {
        continue
      }
      if (isAlbumValid(albumId)) {
        nextAlbums[albumId] = true
      } else {
        albumsChanged = true
      }
    }

    // Prune individually selected files that are confirmed gone.
    let filesChanged = false
    const nextFiles: Record<string, boolean> = {}
    const nextFileAlbums: Record<string, string> = {}
    for (const [fileId, value] of Object.entries(selectedFiles)) {
      if (!value) {
        continue
      }
      const albumId = selectedFileAlbums[fileId]
      const validity = isFileValid(fileId, albumId)
      if (validity === 'invalid') {
        filesChanged = true
      } else {
        nextFiles[fileId] = true
        if (albumId) {
          nextFileAlbums[fileId] = albumId
        }
      }
    }

    if (albumsChanged) {
      setAllSelectedAlbums(nextAlbums)
    }
    if (filesChanged) {
      setSelectedFiles(nextFiles)
      setSelectedFileAlbums(nextFileAlbums)
    }

    if (albumsChanged || filesChanged) {
      const remaining = Object.values(nextFiles).filter(Boolean).length + Object.values(nextAlbums).filter(Boolean).length
      if (remaining === 0) {
        isSelectingRef.current = false
        _setIsSelecting(false)
        _setSelectionMode(SelectionMode.DEFAULT)
      }
    }
  }

  function getSelectedAlbumFileIds(): Array<string> {
    return Object.keys(selectedFiles).filter(file => selectedFiles[file])
  }

  const hasSelection = getSelectedAlbumFileIds().length > 0 || getSelectedAlbumIds().length > 0

  return {
    isSelecting,
    setIsSelecting,
    isAllSelected,
    setIsAllSelected,
    toggleFileSelection,
    getSelectedAlbumFileIds,
    getSelectedAlbumIds,
    isFileSelected,
    maxFiles,
    setMaxFiles,
    selectionMode,
    setSelectionMode,
    hasSelection,
    clearSelection,
    toggleFileInSelection,
    getSelectedFileAlbumId,
    pruneSelection,
    abortSelection,
  }
}

export function getFilterFromSelection(fileSelection: FileSelectionType, albumId?: string): Array<Array<string>> {
  const selectedFiles = fileSelection.getSelectedAlbumFileIds()
  const selectedAlbumIds = fileSelection.getSelectedAlbumIds()

  // Wenn ein spezifisches Album übergeben wurde und dieses komplett selektiert ist
  if (albumId && fileSelection.isAllSelected(albumId)) {
    return []
  }

  // Wenn irgendein Album komplett selektiert ist
  if (selectedAlbumIds.length > 0) {
    return []
  }

  if (selectedFiles.length <= 0) {
    throw 'no selection'
  }

  return [['ids', selectedFiles.join(',')]]
}
